import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { UserNotification } from '../../models/notification.model';
import { NotificationService } from '../../services/apis/notification.service';
import {
  LoadNotifications,
  LoadNotificationsSuccess,
  LoadNotificationsFail,
  LoadUnreadCount,
  LoadUnreadCountSuccess,
  MarkNotificationAsRead,
  MarkNotificationAsReadSuccess,
  MarkAllNotificationsAsRead,
  ReceiveRealtimeNotification,
} from '../actions/notification.actions';

export interface NotificationStateModel {
  items: UserNotification[];
  unreadCount: number;
  totalCount: number;
  pageNumber: number;
  loaded: boolean;
  error: any;
}

@State<NotificationStateModel>({
  name: 'notification',
  defaults: {
    items: [],
    unreadCount: 0,
    totalCount: 0,
    pageNumber: 0,
    loaded: false,
    error: null,
  },
})
@Injectable()
export class NotificationState {
  constructor(private service: NotificationService) {}

  @Selector()
  static getItems(state: NotificationStateModel) {
    return state.items;
  }

  @Selector()
  static getUnreadCount(state: NotificationStateModel) {
    return state.unreadCount;
  }

  @Selector()
  static getTotalCount(state: NotificationStateModel) {
    return state.totalCount;
  }

  @Selector()
  static getLatestNotifications(state: NotificationStateModel) {
    return state.items.slice(0, 2);
  }

  @Action(LoadNotifications)
  load(ctx: StateContext<NotificationStateModel>, action: LoadNotifications) {
    return this.service.getNotifications(action.payload).pipe(
      tap((response) => {
        ctx.dispatch(new LoadNotificationsSuccess(response));
      }),
      catchError((error) => {
        ctx.dispatch(new LoadNotificationsFail(error));
        return throwError(error);
      })
    );
  }

  @Action(LoadNotificationsSuccess)
  loadSuccess(ctx: StateContext<NotificationStateModel>, action: LoadNotificationsSuccess) {
    ctx.patchState({
      items: action.response.items,
      totalCount: action.response.totalCount,
      pageNumber: action.response.pageNumber,
      unreadCount: action.response.unreadCount,
      loaded: true,
      error: null,
    });
  }

  @Action(LoadNotificationsFail)
  loadFail(ctx: StateContext<NotificationStateModel>, action: LoadNotificationsFail) {
    ctx.patchState({
      error: action.error,
      loaded: false,
    });
  }

  @Action(LoadUnreadCount)
  loadUnreadCount(ctx: StateContext<NotificationStateModel>) {
    return this.service.getUnreadCount().pipe(
      tap((count) => {
        ctx.dispatch(new LoadUnreadCountSuccess(count));
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  @Action(LoadUnreadCountSuccess)
  loadUnreadCountSuccess(ctx: StateContext<NotificationStateModel>, action: LoadUnreadCountSuccess) {
    ctx.patchState({
      unreadCount: action.count,
    });
  }

  @Action(MarkNotificationAsRead)
  markAsRead(ctx: StateContext<NotificationStateModel>, action: MarkNotificationAsRead) {
    return this.service.markAsRead(action.notificationId).pipe(
      tap(() => {
        ctx.dispatch(new MarkNotificationAsReadSuccess(action.notificationId));
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  @Action(MarkNotificationAsReadSuccess)
  markAsReadSuccess(ctx: StateContext<NotificationStateModel>, action: MarkNotificationAsReadSuccess) {
    const state = ctx.getState();
    const updatedItems = state.items.map((item) =>
      item.id === action.notificationId ? { ...item, isRead: true } : item
    );
    const unreadCount = Math.max(0, state.unreadCount - 1);
    ctx.patchState({
      items: updatedItems,
      unreadCount,
    });
  }

  @Action(MarkAllNotificationsAsRead)
  markAllAsRead(ctx: StateContext<NotificationStateModel>) {
    return this.service.markAllAsRead().pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedItems = state.items.map((item) => ({ ...item, isRead: true }));
        ctx.patchState({
          items: updatedItems,
          unreadCount: 0,
        });
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  @Action(ReceiveRealtimeNotification)
  receiveRealtime(ctx: StateContext<NotificationStateModel>, action: ReceiveRealtimeNotification) {
    const state = ctx.getState();
    ctx.patchState({
      items: [action.notification, ...state.items],
      unreadCount: state.unreadCount + 1,
      totalCount: state.totalCount + 1,
    });
  }
}
